import React, { useState } from 'react';
import Layout from './Layout';
import { CheckCircle, Clock, AlertCircle, FileText, Upload, Download, Wifi, Database, FileUp } from 'lucide-react';

const MRVFlows: React.FC = () => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [iotConnected, setIotConnected] = useState(false);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      window.alert(`File uploaded: ${file.name}\nSize: ${(file.size / 1024).toFixed(2)} KB\nType: ${file.type}`);
    }
  };

  const handleIotConnect = () => {
    setIotConnected(!iotConnected);
    window.alert(iotConnected ? 'IoT sensors disconnected' : 'IoT sensors connected successfully!\n\nConnected devices:\n- CEMS Sensor (Plant A)\n- Energy Meter (Plant B)\n- Waste Flow Sensor (Plant C)');
  };

  const mrvSteps = [
    {
      id: 1,
      title: 'Data Collection',
      status: 'completed',
      description: 'Automated sensor data collection from steel and cement plants',
      timestamp: '2024-01-15 09:00',
      details: 'CO₂ emissions, energy consumption, waste input data collected'
    },
    {
      id: 2,
      title: 'Verification',
      status: 'in-progress',
      description: 'Third-party verification of emission reduction claims',
      timestamp: '2024-01-15 14:30',
      details: 'Gold Standard methodology applied for verification'
    },
    {
      id: 3,
      title: 'Reporting',
      status: 'pending',
      description: 'Generate standardized MRV reports for international compliance',
      timestamp: 'Scheduled for 2024-01-16',
      details: 'Article 6 compliant reporting format'
    },
    {
      id: 4,
      title: 'Registry Submission',
      status: 'pending',
      description: 'Submit verified credits to international carbon registry',
      timestamp: 'Pending verification completion',
      details: 'Integration with Verra and Gold Standard registries'
    }
  ];

  const reports = [
    {
      name: 'Q4 2024 MRV Report - Steel Plant A',
      type: 'Quarterly Report',
      status: 'Verified',
      co2Reduction: '1,247 tCO₂e',
      date: '2024-01-10'
    },
    {
      name: 'Biogas Integration Impact Assessment',
      type: 'Special Report',
      status: 'Under Review',
      co2Reduction: '892 tCO₂e',
      date: '2024-01-12'
    },
    {
      name: 'Circular Waste Pathway Analysis',
      type: 'Technical Report',
      status: 'Draft',
      co2Reduction: '1,456 tCO₂e',
      date: '2024-01-14'
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'in-progress':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'pending':
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Verified':
        return 'bg-green-100 text-green-800';
      case 'Under Review':
        return 'bg-yellow-100 text-yellow-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">MRV Flows</h1>
          <div className="flex space-x-3">
            <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Upload className="h-4 w-4 mr-2" />
              Upload Data
            </button>
            <button className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </button>
          </div>
        </div>

        {/* IoT Sensor Integration */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Wifi className={`h-6 w-6 ${iotConnected ? 'text-green-600' : 'text-gray-400'}`} />
              <h2 className="text-lg font-semibold text-gray-900">IoT Sensor Integration</h2>
            </div>
            <button
              onClick={handleIotConnect}
              className={`px-4 py-2 rounded-lg font-medium ${
                iotConnected
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {iotConnected ? 'Disconnect' : 'Connect IoT Sensors'}
            </button>
          </div>
          
          {iotConnected && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-gray-900">CEMS Sensor</span>
                </div>
                <p className="text-sm text-gray-600">Plant A - Steel Production</p>
                <p className="text-xs text-green-600 mt-1">● Live - 2,450 tCO₂/day</p>
              </div>
              
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-gray-900">Energy Meter</span>
                </div>
                <p className="text-sm text-gray-600">Plant B - Cement Kiln</p>
                <p className="text-xs text-green-600 mt-1">● Live - 1,850 MWh/day</p>
              </div>
              
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-gray-900">Waste Flow Sensor</span>
                </div>
                <p className="text-sm text-gray-600">Plant C - Waste Processing</p>
                <p className="text-xs text-green-600 mt-1">● Live - 450 tons/day</p>
              </div>
            </div>
          )}
        </div>

        {/* Manual Data Upload */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center gap-3 mb-4">
            <FileUp className="h-6 w-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Manual Data Upload</h2>
          </div>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept=".csv,.xlsx,.xls,.json"
              onChange={handleFileUpload}
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">
                Click to upload or drag and drop
              </p>
              <p className="text-sm text-gray-500">
                CSV, Excel, or JSON files (Max 10MB)
              </p>
            </label>
          </div>

          {uploadedFile && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-gray-900">{uploadedFile.name}</p>
                    <p className="text-sm text-gray-600">
                      {(uploadedFile.size / 1024).toFixed(2)} KB - Uploaded successfully
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => window.alert('Processing data...\n\nData validated and imported into MRV system.')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Process Data
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700">Supported Formats</p>
              <p className="text-xs text-gray-500 mt-1">CSV, Excel, JSON</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700">Data Types</p>
              <p className="text-xs text-gray-500 mt-1">Emissions, Energy, Waste</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700">Auto-Validation</p>
              <p className="text-xs text-gray-500 mt-1">ISO 14064 compliant</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">MRV Process Flow</h2>
          <div className="space-y-6">
            {mrvSteps.map((step, index) => (
              <div key={step.id} className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  {getStatusIcon(step.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900">{step.title}</h3>
                    <span className="text-xs text-gray-500">{step.timestamp}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                  <p className="text-xs text-gray-500 mt-2">{step.details}</p>
                </div>
                {index < mrvSteps.length - 1 && (
                  <div className="absolute left-2 mt-8 w-px h-6 bg-gray-300"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Monitoring Metrics</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Data Points Collected</span>
                <span className="font-semibold">24,567</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Verification Rate</span>
                <span className="font-semibold text-green-600">98.7%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Compliance Score</span>
                <span className="font-semibold text-blue-600">94/100</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reporting Status</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Reports Generated</span>
                <span className="font-semibold">47</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Verified Reports</span>
                <span className="font-semibold text-green-600">42</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pending Review</span>
                <span className="font-semibold text-yellow-600">5</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Verification Partners</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-sm">Gold Standard</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span className="text-sm">Verra (VCS)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <span className="text-sm">Article 6 Registry</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent MRV Reports</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Report Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CO₂ Reduction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reports.map((report, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-gray-400 mr-3" />
                        <div className="text-sm font-medium text-gray-900">{report.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {report.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(report.status)}`}>
                        {report.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {report.co2Reduction}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {report.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button className="text-blue-600 hover:text-blue-900 mr-3">View</button>
                      <button className="text-green-600 hover:text-green-900">Download</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MRVFlows;